import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateMaterial } from "@/api/hooks/useCennik";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import type { Material } from "@/api/schemas";

/**
 * Alias obľúbeného kovania — vlastné pomenovanie, podľa ktorého sa
 * kovanie dá aj vyhľadať (je súčasťou fulltextu). Otvára sa po
 * pridaní hviezdičky a cez tlačidlo úpravy aliasu pri obľúbených.
 */
export function AliasDialog({
  material,
  onClose,
}: {
  material: Material | null;
  onClose: () => void;
}): JSX.Element {
  const updateMutation = useUpdateMaterial();
  const [alias, setAlias] = useState("");

  useEffect(() => {
    if (material) setAlias(material.alias ?? "");
  }, [material]);

  async function save(): Promise<void> {
    if (!material) return;
    try {
      await updateMutation.mutateAsync({
        id: material.id,
        data: { alias: alias.trim() || null },
      });
      toast({
        title: alias.trim() ? "Alias uložený" : "Alias odstránený",
        description: alias.trim() || material.nazov,
      });
      onClose();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní aliasu",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={!!material} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Alias kovania</DialogTitle>
          <DialogDescription className="sr-only">
            Vlastné pomenovanie obľúbeného kovania — hľadá sa podľa neho aj vo fulltexte.
          </DialogDescription>
        </DialogHeader>
        <p className="truncate text-sm text-muted-foreground" title={material?.nazov}>
          {material?.nazov}
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="kov-alias">Alias (vlastné pomenovanie)</Label>
          <Input
            id="kov-alias"
            autoFocus
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
            }}
            placeholder="napr. záves kuchyňa spodné skrinky"
            maxLength={100}
          />
          <p className="text-[11px] text-muted-foreground">
            Podľa aliasu sa kovanie dá vyhľadať všade, kde sa hľadá
            v cenníku.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Preskočiť
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Ukladá sa…" : "Uložiť alias"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
